import { isQuantitativeType, calculateProgressMetrics, mergeFrameworkAnswers } from "./dashboardHelpers";

/**
 * Pure recomputation of the dashboard's client-side metric slice used both to render
 * the KPI cards / monthly activity summary and to feed the coverage-by-module fallback.
 *
 * This intentionally rebuilds question/user/answer derivations locally (rather than reusing
 * the component-level memoized selectors) because it applies a different CUSTOM-question
 * expansion (location×period) and stricter ACCEPTED/status filtering than the trend selectors.
 *
 * @returns {{
 *   filteredQuestions: object[],
 *   mergedFrameworkAnswers: object[],
 *   filteredUsers: number[],
 *   fromDates: string[],
 *   totalQuestions: number,
 *   overallCompletion: string,
 *   totalActiveUsers: number,
 *   totalPending: number,
 *   monthlyActivities: object[]
 * }}
 */
export const computeLocalMetrics = ({
  reportingQuestions,
  allUsers,
  selectedLocations,
  selectedModules,
  locationOptions,
  periodOptions,
  selectedPeriodsValue,
  reportingAnswers,
  locations,
  reportingAssignDetails,
}) => {
  const uniqueQuestionSet = new Set();
  const totalQuestionForCompany = [];
  if (Array.isArray(reportingQuestions)) {
    reportingQuestions.forEach((item) => {
      const key = `${item.questionId}-${item.moduleId}-${item.frequency}`;
      if (!uniqueQuestionSet.has(key)) {
        uniqueQuestionSet.add(key);
        let frameworksIds = [];
        try {
          frameworksIds = JSON.parse(item.mapFrameworkIds || '[]');
        } catch (e) {
          console.warn(`Invalid mapFrameworkIds for question ID ${item.questionId}: ${item.mapFrameworkIds}`);
        }
        totalQuestionForCompany.push({
          questionId: item.questionId,
          moduleId: item.moduleId,
          frequency: item.frequency,
          questionType: item.questionType,
          frameworksIds
        });
      }
    });
  }

  const userResult = allUsers.map(user => {
    let sourceId = [];
    try {
      if (user.source_ids) {
        sourceId = JSON.parse(user.source_ids);
      }
    } catch (e) {
      console.warn(`Invalid source_ids for user ID ${user.id}: ${user.source_ids}`);
    }

    return {
      id: user.id,
      sourceId
    };
  });

  const effectiveLocationIds = selectedLocations.length === 0
    ? locations.map(l => l.id)
    : selectedLocations;

  const filteredUsers = selectedLocations.length === 0
    ? userResult.map(item => item.id)
    : userResult.filter(user =>
      user.sourceId.some(sid => effectiveLocationIds.includes(sid))
    ).map(item => item.id);

  const filteredQuestions = [];

  for (const q of totalQuestionForCompany) {
    const isModuleMatched = selectedModules.length === 0 || selectedModules.includes(q.moduleId);

    if (isModuleMatched) {
      let check1 = selectedLocations.length === 0 ? locationOptions.length : selectedLocations.length;
      let check2 = selectedPeriodsValue.length === 0 ? periodOptions.length : selectedPeriodsValue.length;

      const weight = q.frequency === "CUSTOM" ? check1 * check2 : 1;
      if (weight > 0) {
        filteredQuestions.push({ ...q, weight });
      }
    }
  }

  const customQuestionIds = filteredQuestions
    .filter(item => item.frequency === "CUSTOM" && !isQuantitativeType(item.questionType))
    .map(item => item.questionId);

  const questionIdsOthers = filteredQuestions
    .filter(item => item.frequency !== "CUSTOM" || isQuantitativeType(item.questionType))
    .map(item => item.questionId);
  const fromDates = selectedPeriodsValue.map(p => p.fromDate);

  const { totalDataPoints, completedCount } = calculateProgressMetrics(
    totalQuestionForCompany.filter(q => {
      const isModuleMatched = selectedModules.length === 0 || selectedModules.includes(q.moduleId);
      return isModuleMatched;
    }),
    reportingAnswers,
    effectiveLocationIds,
    selectedPeriodsValue,
    filteredUsers,
    isQuantitativeType
  );

  const totalQuestions = totalDataPoints;
  const totalCompleted = completedCount;

  const overallCompletion = totalQuestions > 0
    ? ((totalCompleted / totalQuestions) * 100).toFixed(2)
    : "0.00";

  const questionIds = filteredQuestions.map(q => q.questionId);

  const matchingAssignments = reportingAssignDetails.filter(item =>
    questionIds.includes(item.questionId)
  );

  const userIds = allUsers
    .filter(user => {
      try {
        const userSourceIds = JSON.parse(user.source_ids || "[]");
        return userSourceIds.some((id) => effectiveLocationIds.includes(id));
      } catch {
        return false;
      }
    })
    .map(user => user.id);

  const assignedToList = [
    ...new Set(matchingAssignments.flatMap(item => item.assignedTo))
  ];

  const filtered = assignedToList.filter(id => userIds.includes(Number(id)));

  const totalActiveUsers = filtered.length;

  const totalPending = totalQuestions - totalCompleted;

  const now = new Date();
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  const monthLabel = lastMonthStart.toLocaleString('default', { month: 'long', year: 'numeric' });

  const relevantAnswers = reportingAnswers.filter(item => {
    const isRelevantQuestion = questionIds.includes(item.questionId);
    const isRelevantUser = filteredUsers.includes(item.userId);

    if (customQuestionIds.includes(item.questionId)) {
      return isRelevantQuestion && isRelevantUser && fromDates.includes(item.fromDate);
    }

    return isRelevantQuestion && isRelevantUser;
  });

  const completedThisMonth = relevantAnswers.filter(item => {
    if (item.status !== 'ACCEPTED' || !item.updatedAt) return false;
    const updatedDate = new Date(item.updatedAt);
    return updatedDate >= lastMonthStart && updatedDate <= lastMonthEnd;
  }).length;

  const inProgressThisMonth = relevantAnswers.filter(item => {
    if (!item.updatedAt) return false;
    const submissionStatus = (item.submissionStatus || '').toLowerCase();
    if (submissionStatus !== 'sent_to_audit') return false;
    const updatedDate = new Date(item.updatedAt);
    return updatedDate >= lastMonthStart && updatedDate <= lastMonthEnd;
  }).length;

  const monthlyActivities = [
    {
      type: 'completed',
      count: completedThisMonth,
      label: 'Completed',
      timeframe: monthLabel
    },
    {
      type: 'progress',
      count: inProgressThisMonth,
      label: 'In Audit',
      timeframe: monthLabel
    },
  ];

  const mergedFrameworkAnswers = mergeFrameworkAnswers(
    reportingAnswers,
    questionIdsOthers,
    customQuestionIds,
    fromDates,
    effectiveLocationIds
  );

  return {
    filteredQuestions,
    mergedFrameworkAnswers,
    filteredUsers,
    fromDates,
    totalQuestions,
    overallCompletion,
    totalActiveUsers,
    totalPending,
    monthlyActivities,
  };
};
